'use client';

import { useLayoutEffect, useRef } from 'react';

const toggleOverflowClass = (elem: HTMLDivElement) => {
  elem.classList.toggle('overflowing', elem.scrollWidth > elem.clientWidth);
};

const DEFAULT_OPTIONS = {
  config: { attributes: true, childList: true, subtree: true },
};

export function useResizeObserver<T extends HTMLDivElement>(
  options = DEFAULT_OPTIONS,
) {
  const ref = useRef<T>(null);

  useLayoutEffect(() => {
    const element = ref?.current;

    if (!element) {
      return;
    }
    if (!window.ResizeObserver) return;

    const observer = new ResizeObserver((entries) => {
      toggleOverflowClass(entries[0].target as HTMLDivElement);
    });

    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [ref]);

  useLayoutEffect(() => {
    const element = ref?.current;

    if (!element) {
      return;
    }
    if (!window.MutationObserver) return;

    const observer = new MutationObserver((entries) => {
      toggleOverflowClass(entries[0].target as HTMLDivElement);
    });

    const { config } = options;
    observer.observe(element, config);
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [ref, options]);

  return ref;
}
